/**
         * Strapi v5 Helper Functions
         * Utility functions for working with Strapi API responses and data structures
         */

        // Extract image URL from Strapi media field
        export const getStrapiImageUrl = (imageData, size = 'medium') => {
            if (!imageData) return null;

            // Handle direct URL string
            if (typeof imageData === 'string') {
                // If it's a relative URL, prepend the Strapi base URL
                if (imageData.startsWith('/uploads/')) {
                    const strapiUrl = import.meta.env?.VITE_STRAPI_API_URL || '';
                    return `${strapiUrl}${imageData}`;
                }
                return imageData;
            }

            // Handle Strapi v5 media object structure (data.attributes)
            if (imageData?.data?.attributes) {
                const formats = imageData?.data?.attributes?.formats;
                const mainUrl = imageData?.data?.attributes?.url;

                // Return specific size if available
                if (formats && formats?.[size]) {
                    const url = formats?.[size]?.url;
                    return url?.startsWith('/') ? `${import.meta.env?.VITE_STRAPI_API_URL || ''}${url}` : url;
                }

                // Fallback to main URL
                return mainUrl?.startsWith('/') ? `${import.meta.env?.VITE_STRAPI_API_URL || ''}${mainUrl}` : mainUrl;
            }

            // Handle direct media object (without data wrapper)
            if (imageData?.url) {
                const formats = imageData?.formats;
                const mainUrl = imageData?.url;

                // Return specific size if available
                if (formats && formats?.[size]) {
                    const url = formats?.[size]?.url;
                    return url?.startsWith('/') ? `${import.meta.env?.VITE_STRAPI_API_URL || ''}${url}` : url;
                }

                // Fallback to main URL
                return mainUrl?.startsWith('/') ? `${import.meta.env?.VITE_STRAPI_API_URL || ''}${mainUrl}` : mainUrl;
            }

            // Handle array of images (get first one)
            if (Array.isArray(imageData) && imageData?.length > 0) {
                return getStrapiImageUrl(imageData?.[0], size);
            }

            return null;
        };

        // Extract multiple image URLs from Strapi media field
        export const getStrapiImageUrls = (imagesData, size = 'medium') => {
            if (!imagesData) return [];
            
            if (Array.isArray(imagesData)) {
                return imagesData?.map(img => getStrapiImageUrl(img, size))?.filter(Boolean);
            }
            
            const singleUrl = getStrapiImageUrl(imagesData, size);
            return singleUrl ? [singleUrl] : [];
        };

        // Format Strapi collection response
        export const formatStrapiResponse = (response) => {
            if (!response) return null;
            
            // Handle single item response
            if (response?.data && !Array.isArray(response?.data)) {
                return {
                    id: response?.data?.id,
                    ...response?.data?.attributes,
                    meta: response?.meta,
                };
            }
            
            // Handle collection response
            if (response?.data && Array.isArray(response?.data)) {
                const items = response?.data?.map(item => ({
                    id: item?.id,
                    ...item?.attributes,
                }));
                
                return {
                    data: items,
                    meta: response?.meta,
                    pagination: response?.meta?.pagination,
                };
            }
            
            return response;
        };

        // Build Strapi query parameters
        export const buildStrapiQuery = ({
            populate = [],
            filters = {},
            sort = [],
            pagination = {},
            fields = [],
            publicationState = 'live',
        } = {}) => {
            const params = {};
            
            // Populate relations
            if (populate?.length > 0) {
                if (Array.isArray(populate)) {
                    params.populate = populate?.join(',');
                } else {
                    params.populate = populate;
                }
            }
            
            // Add filters
            Object.keys(filters)?.forEach(key => {
                params[`filters[${key}]`] = filters?.[key];
            });
            
            // Add sorting
            if (sort?.length > 0) {
                params.sort = Array.isArray(sort) ? sort?.join(',') : sort;
            }
            
            // Add pagination
            if (pagination?.page) params['pagination[page]'] = pagination?.page;
            if (pagination?.pageSize) params['pagination[pageSize]'] = pagination?.pageSize;
            if (pagination?.start) params['pagination[start]'] = pagination?.start;
            if (pagination?.limit) params['pagination[limit]'] = pagination?.limit;
            
            // Add field selection
            if (fields?.length > 0) {
                params.fields = Array.isArray(fields) ? fields?.join(',') : fields;
            }
            
            // Publication state
            if (publicationState) {
                params.publicationState = publicationState;
            }
            
            return params;
        };

        // Handle Strapi API errors
        export const handleStrapiError = (error) => {
            if (!error?.response) {
                return {
                    message: 'Network error. Please check your connection.',
                    details: error?.message,
                };
            }
            
            const { data, status } = error?.response;
            
            // Handle validation errors
            if (status === 400 && data?.error?.details) {
                return {
                    message: 'Validation failed',
                    details: data?.error?.details,
                    validationErrors: data?.error?.details?.errors || [],
                };
            }
            
            // Handle authentication errors
            if (status === 401) {
                return {
                    message: 'Authentication required',
                    details: data?.error?.message || 'Please log in to continue',
                };
            }
            
            // Handle authorization errors
            if (status === 403) {
                return {
                    message: 'Access denied',
                    details: data?.error?.message || 'You do not have permission to perform this action',
                };
            }
            
            // Handle not found errors
            if (status === 404) {
                return {
                    message: 'Resource not found',
                    details: data?.error?.message || 'The requested resource was not found',
                };
            }
            
            // Handle server errors
            if (status >= 500) {
                return {
                    message: 'Server error',
                    details: data?.error?.message || 'An internal server error occurred',
                };
            }
            
            // Generic error handling
            return {
                message: data?.error?.message || 'An error occurred',
                details: data?.error?.details || error?.message,
                status,
            };
        };

        // Convert local date to Strapi format
        export const toStrapiDate = (date) => {
            if (!date) return null;
            return new Date(date)?.toISOString();
        };

        // Convert Strapi date to local format
        export const fromStrapiDate = (strapiDate) => {
            if (!strapiDate) return null;
            return new Date(strapiDate);
        };

        // Format Strapi relation data
        export const formatStrapiRelation = (relationData) => {
            if (!relationData) return null;
            
            // Single relation
            if (relationData?.data && !Array.isArray(relationData?.data)) {
                return {
                    id: relationData?.data?.id,
                    ...relationData?.data?.attributes,
                };
            }
            
            // Multiple relations
            if (relationData?.data && Array.isArray(relationData?.data)) {
                return relationData?.data?.map(item => ({
                    id: item?.id,
                    ...item?.attributes,
                }));
            }
            
            return relationData;
        };